import { BankSlipData, FavoritePIXAccount, FavoritesFilters } from "@/models";
import { cleanDocument, filterAndPageableToRequest, detectPixKeyType, normalizePhoneForAPI } from "@/utils";
import { createContext, ReactNode, useCallback, useContext, useState} from "react";
import { format } from "date-fns";
import { Api_Banking } from "@/services/Api/bankingApiConfig";
import { AxiosResponse } from "axios";
import { useDisclosure } from "@chakra-ui/react";
  interface PaymentProps {
    children: ReactNode;
  }
  interface PixRecipient {
    isFavorite?: boolean;
    amount?: number;
    pixKey?: string;
    transferId?: string;
    endToEndId?: string;
    description?: string | null;
    user?: string;
    recipientName?: string;
    recipientDocument?: string;
    payerName?: string;
    payerDocument?: string;
    bankIspb?: string;
    costCenter?: string;
    paymentDate?: string | Date;
  }
  interface CheckPaymentPayload {
    type: string;
    barCode?: string;
    pixKey?: string;
    pixAmount?: number;
    description?: string;
    isFavorite?: boolean;
    pixQRCode?: string;
  }
  interface PayPaymentPayload {
    transferId: string;
    costCenter: string;
    paymentDate: string;
    description: string;
    isFavorite: boolean;
  }
  interface PaymentBatchListResponse {
    id: number;
    batchUploadDate: string;
    name: string;
    filename: string;
    paymentsTotalAmount: number;
  }
  interface BatchError {
    message: string;
    line?: number;
    field?: string;
  }
  interface PaymentTransaction {
    id?: number;
    transferId?: string;
    amount?: number;
    status?: string;
    recipientName?: string;
    recipientDocument?: string;
    description?: string;
    scheduledDate?: string;
    [key: string]: any;
  }
  interface PaymentBatchDetailResponse {
    id: number;
    name: string;
    fileUrl: string;
    filename: string;
    numPaymentsSuccess: number;
    numPaymentsTotal: number;
    paymentsTotalAmount: number;
    errors: BatchError[];
    paymentTransactions: PaymentTransaction[];
    batchUploadDate: string;
  }
  interface PaymentContextData {
    cancelPayment: (uuid: string) => Promise<AxiosResponse<any, any>>;
    getListPaymentsInitialData: (filter: object, pageable: object) => Promise<void>;
    getPaymentBatches: (pageable: object) => Promise<AxiosResponse<any, any>>;
    getListPaymentsBatchesInitialData: (pageable: object) => Promise<void>;
    getPaymentBatchDetail: (id: number) => Promise<AxiosResponse<any, any>>;
    getPaymentBatchDetailInitialData: (id: number) => Promise<void>;
    createPaymentBatch: (fileName: string, file: File | Blob, otherData?: any) => Promise<AxiosResponse<any, any>>;
    getListPixFavoritesInitialData: () => Promise<void>;
    handleEditPixRecipient: (
      updates: Partial<{
        description: string;
        paymentDate: string | Date;
        costCenter: string;
        isFavorite: boolean;
      }>
    ) => void;
    handleEditBankSlipRecipient: (
      updates: Partial<{
        paymentDate: string | Date;
        costCenter: string;
      }>
    ) => void;
    checkPayment: (payload: CheckPaymentPayload) => Promise<void>;
    processPayment: (type: "PIX" | "BANK_SLIP", payload: any) => Promise<any>;
    processPaymentsApproval: (transferIds: string[]) => Promise<any>;
    isNewPaymentDrawerOpen: boolean;
    onNewPaymentDrawerOpen: () => void;
    onNewPaymentDrawerClose: () => void;
    isNewPaymentBatchDrawerOpen: boolean;
    onNewPaymentBatchDrawerOpen: () => void;
    onNewPaymentBatchDrawerClose: () => void;
    isDetailsPaymentBatchDrawerOpen: boolean;
    onDetailsPaymentBatchDrawerOpen: () => void;
    onDetailsPaymentBatchDrawerClose: () => void;
    submitPaymentBatch: (batchId: number) => void;
    isPaymentsLoading: boolean;
    isPaymentBatchesLoading: boolean;
    isPaymentBatchDetailLoading: boolean;
    isListPixFavoritesLoading: boolean;
    payments: any[];
    paymentBatches: PaymentBatchListResponse[];
    paymentBatchDetail: PaymentBatchDetailResponse | null;
    requestedPayments: any[];
    pixFavorites: any[];
    pixRecipient: PixRecipient;
    bankSlipData: BankSlipData;
    paginationInfo: {
      totalPages: number;
      totalElements: number;
      currentPage: number;
      size: number;
    };
    batchesPaginationInfo: {
      totalPages: number;
      totalElements: number;
      currentPage: number;
      size: number;
    };
  }

  const PaymentContext = createContext<PaymentContextData>(
    {} as PaymentContextData
  );

  //CACHE FAVORITE PIX
  let favoritesCache: { data: FavoritePIXAccount[]; timestamp: number } | null = null;
  const CACHE_DURATION = 30 * 60 * 1000; // 30 minutos

  const PaymentProvider = ({ children }: PaymentProps) => {
      const user = JSON.parse(localStorage.getItem("barte-user-info"));
      const [isPaymentsLoading, setIsPaymentsLoading] = useState(false);
      const [isPaymentBatchesLoading, setIsPaymentBatchesLoading] = useState(false);
      const [isPaymentBatchDetailLoading, setIsPaymentBatchDetailLoading] = useState(false);
      const [isListPixFavoritesLoading, setListPixFavoritesLoading] = useState(false);
      const [payments, setPayments] = useState([]);
      const [paymentBatches, setPaymentBatches] = useState<PaymentBatchListResponse[]>([]);
      const [paymentBatchDetail, setPaymentBatchDetail] = useState<PaymentBatchDetailResponse | null>(null);
      const [requestedPayments, setRequestedPayments] = useState([]);
      const [pixFavorites, setPixFavorites] = useState([]);
      const [paginationInfo, setPaginationInfo] = useState({
        totalPages: 0,
        totalElements: 0,
        currentPage: 0,
        size: 0
      });
      const [batchesPaginationInfo, setBatchesPaginationInfo] = useState({
        totalPages: 0,
        totalElements: 0,
        currentPage: 0,
        size: 0
      });
      const [pixRecipient, setPixRecipient] = useState<PixRecipient | null>(null);
      const [filters, setFilters] = useState < FavoritesFilters > ({
        search: '',
        active: undefined, // Mostrar todos os favoritos por padrão (ativos e inativos)
        sortBy: 'nickname',
        sortOrder: 'asc'
      });

      //Disclosures
      const { isOpen: isNewPaymentDrawerOpen, onOpen: onNewPaymentDrawerOpen, onClose: onNewPaymentDrawerClose } = useDisclosure();
      const { isOpen: isNewPaymentBatchDrawerOpen, onOpen: onNewPaymentBatchDrawerOpen, onClose: onNewPaymentBatchDrawerClose } = useDisclosure();
      const { isOpen: isDetailsPaymentBatchDrawerOpen, onOpen: onDetailsPaymentBatchDrawerOpen, onClose: onDetailsPaymentBatchDrawerClose } = useDisclosure();
      //Api's Call
      const getPayments = (filter: object, pageable: object) => { return Api_Banking.get(`payments${filterAndPageableToRequest({ filter, pageable })}`)}
      const getPaymentBatches = (pageable: object) => { return Api_Banking.get(`payments/batch${filterAndPageableToRequest({ filter: {}, pageable })}`)}
      const getPaymentBatchDetail = (id: number) => { return Api_Banking.get(`payments/batch/${id}`)}
      const createPaymentBatch = (fileName: string, file: File | Blob, otherData: any = {}) => {
        const url = `payments/batch/create`;
        const formData = new FormData();
        formData.append('file', file);
        formData.append('fileName', fileName);
        Object.entries(otherData).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            formData.append(key, value as any);
          }
        });
        return Api_Banking.post(url, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
      }
      const submitPaymentBatch = ( batchId: any) => { return Api_Banking.post(`payments/batch/${batchId}`,) }
      const validatePayment = (body: any) => { return Api_Banking.post(`payments/create`, body) } 
      const cancelPayment = (uuid: string) => { return Api_Banking.delete(`payments/${uuid}`) }
      const listPixFavorites = (filter: object, pageable: object) => { return Api_Banking.get(`payments/favorites${filterAndPageableToRequest({ filter, pageable })}`) }
      const payPayment = (body: PayPaymentPayload) => { return Api_Banking.post(`payments`, body) }
      const approvePayments = (transferIds: string[]) => { return Api_Banking.post(`payments/approval`, transferIds) }

      async function getListPaymentsInitialData(filter: object, pageable: object) {
          setIsPaymentsLoading(true);
          try {
              const promises = [
                  getPayments(filter, pageable),
              ];
              const [paymentsRes] = await Promise.allSettled(promises);
              // Payments
              if (paymentsRes.status === "fulfilled") {
                  const responseData = paymentsRes.value?.data;
                  const paymentsData = responseData?.content || [];
                  setPayments(paymentsData);
                  if (responseData) {
                      setPaginationInfo({
                          totalPages: responseData.totalPages || 0,
                          totalElements: responseData.totalElements || 0,
                          currentPage: responseData.number || 0,
                          size: responseData.size || 0
                      });
                  }

              } else {
                  //Error handler
              }
              setIsPaymentsLoading(false);
          } catch (e) {
              setIsPaymentsLoading(false);
          }
      }

      async function getListPaymentsBatchesInitialData(pageable: object) {
          setIsPaymentBatchesLoading(true);
          try {
              const promises = [
                  getPaymentBatches(pageable),
              ];
              const [batchesRes] = await Promise.allSettled(promises);
              // Payment Batches
              if (batchesRes.status === "fulfilled") {
                  const responseData = batchesRes.value?.data;
                  const batchesData = responseData?.content || [];
                  setPaymentBatches(batchesData);
                  if (responseData) {
                      setBatchesPaginationInfo({
                          totalPages: responseData.totalPages || 0,
                          totalElements: responseData.totalElements || 0,
                          currentPage: responseData.number || 0,
                          size: responseData.size || 0
                      });
                  }

              } else {
                  //Error handler
              }
              setIsPaymentBatchesLoading(false);
          } catch (e) {
              setIsPaymentBatchesLoading(false);
          }
      }

      async function getPaymentBatchDetailInitialData(id: number) {
          setIsPaymentBatchDetailLoading(true);
          try {
              const promises = [
                  getPaymentBatchDetail(id),
              ];
              const [batchDetailRes] = await Promise.allSettled(promises);
              // Payment Batch Detail
              if (batchDetailRes.status === "fulfilled") {
                  const responseData = batchDetailRes.value?.data;
                  const formattedData = {
                    ...responseData,
                    paymentTransactions: responseData.paymentTransactions.filter((transaction: any) => transaction.status !== "FAILED"),
                  }
                  if (responseData) {
                      setPaymentBatchDetail(formattedData);
                  }
              } else {
                  //Error handler
                  setPaymentBatchDetail(null);
              }
              setIsPaymentBatchDetailLoading(false);
          } catch (e) {
              setIsPaymentBatchDetailLoading(false);
              setPaymentBatchDetail(null);
          }
      }

      const getListPixFavoritesInitialData = useCallback(async() => {
        try {
          const now = Date.now();
          if (favoritesCache && now - favoritesCache.timestamp < CACHE_DURATION) {
            let filteredData = favoritesCache.data;

            if (filters.search) {
              const search = filters.search.toLowerCase();
              filteredData = filteredData.filter(
                (f) =>
                  f.recipientName.toLowerCase().includes(search) ||
                  f.recipientName.toLowerCase().includes(search) ||
                  f.pixKey.toLowerCase().includes(search)
              );
            }

            if (filters.active !== undefined) {
              filteredData = filteredData.filter((f) => f.active === filters.active);
            }
            setPixFavorites(filteredData);
          }

          const query: {
            active?: string;
            sortBy: string;
            sortOrder: string;
            search?: string;
            keyType?: string;
          } = {
            sortBy: filters.sortBy || "nickname",
            sortOrder: filters.sortOrder || "asc",
          };

          if (filters.active !== undefined) {
            query.active = String(filters.active);
          }

          if (filters.search) {
            query.search = filters.search;
          }

          if (filters.keyType) {
            query.keyType = filters.keyType;
          }
          try {
            const params = new URLSearchParams();
            Object.entries(query).forEach(([key, value]) => {
              if (value !== undefined) {
                params.append(key, String(value));
              }
            });
          
            const response = await listPixFavorites({name: ""}, { page: 0, size: 10000 });
        
            if (response.data) {
              const data = response.data.content || [];
              setPixFavorites(data);
            }
          } catch (error) {
            throw error;
          }
        } catch (err) {
          setPixFavorites([]);
        } finally {
          setListPixFavoritesLoading(false);
        }
      }, [filters])

      async function checkPayment(payload: CheckPaymentPayload) {
        try {
          let formData = { ...payload };
          if (payload.type === "PIX") { 
            let normalizedPixKey = payload?.pixKey || "";
            const keyDigits = cleanDocument(normalizedPixKey);
            const isDocument = keyDigits.length === 11 || keyDigits.length === 14;
            if (!isDocument && detectPixKeyType(normalizedPixKey) === "PHONE") {
              normalizedPixKey = normalizePhoneForAPI(normalizedPixKey);
            }
            formData = {
              ...formData,
              pixKey: isDocument ? keyDigits : normalizedPixKey,
              isFavorite: payload.isFavorite,
            };
            const { data } = await validatePayment(formData);
            setPixRecipient({ ...data, isFavorite: payload.isFavorite });
          }
          if (payload.type === "COPY_PASTE_PIX") {
            // Para QR Code, envia o QR Code completo no campo pixKey e o tipo correto
            formData = {
              type: "COPY_PASTE_PIX",
              pixKey: payload.pixKey, // QR Code completo
              pixAmount: payload.pixAmount,
              description: payload.description,
            };
            const { data } = await validatePayment(formData);
            setPixRecipient({ ...data });
          }
          if (payload.type === "BANK_SLIP") {
            const { data } = await validatePayment(formData);
            setBankSlipData(data);
          }
        } catch (error: any) {
          throw error;
        }
      }

      async function handleEditPixRecipient(
        updates: Partial<Pick<
          typeof pixRecipient,
          "description" | "paymentDate" | "costCenter" | "isFavorite"
        >>
      ) {
        setPixRecipient((prev) => {
          if (!prev) return prev; // nada pra atualizar
          return {
            ...prev,
            ...updates,
            paymentDate: updates.paymentDate
              ? format(new Date(updates.paymentDate), "yyyy-MM-dd") // normaliza a data
              : prev.paymentDate,
            description:
              updates.description !== undefined
                ? updates.description.trim()
                : prev.description,
            costCenter:
              updates.costCenter !== undefined
                ? updates.costCenter.trim()
                : prev.costCenter,
            isFavorite:
              updates.isFavorite !== undefined
                ? updates.isFavorite
                : prev.isFavorite,
          };
        });
      }

      async function handleEditBankSlipRecipient(
        updates: Partial<Pick<
          typeof bankSlipData,
           | "paymentDate" | "costCenter"
        >>
      ) {
        setBankSlipData((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            ...updates,
            paymentDate: updates.paymentDate
              ? format(new Date(`${updates.paymentDate}T00:00:00`), "yyyy-MM-dd")
              : prev.paymentDate,
            costCenter:
              updates.costCenter !== undefined
                ? updates.costCenter.trim()
                : prev.costCenter,
          };
        });
      }

      async function processPayment(type: "PIX" | "BANK_SLIP", payload: any) {
        try {
          if (type === "PIX") {
            return payPayment(payload);
          }
          if (type === "BANK_SLIP") {
            return payPayment(payload);
          }
        } catch (error) {
          throw error;
        }
      }

      //BANK SLIP
      const [bankSlipData, setBankSlipData] = useState<BankSlipData>(null);

      async function processPaymentsApproval(transferIds: string[]) {
        try {
          return approvePayments(transferIds);
        } catch (error) {
          throw error;
        } 
      }
      
      return (
          <PaymentContext.Provider
              value={{
                  getListPaymentsInitialData,
                  getPaymentBatches,
                  getListPaymentsBatchesInitialData,
                  getPaymentBatchDetail,
                  getPaymentBatchDetailInitialData,
                  createPaymentBatch,
                  submitPaymentBatch,
                  getListPixFavoritesInitialData,
                  handleEditPixRecipient,
                  handleEditBankSlipRecipient,
                  cancelPayment,
                  checkPayment,
                  processPayment,
                  processPaymentsApproval,
                  isPaymentsLoading,
                  isPaymentBatchesLoading,
                  isPaymentBatchDetailLoading,
                  payments,
                  paymentBatches,
                  paymentBatchDetail,
                  requestedPayments,
                  pixFavorites,
                  isListPixFavoritesLoading,
                  pixRecipient,
                  bankSlipData,
                  paginationInfo,
                  batchesPaginationInfo,
                  isNewPaymentDrawerOpen,
                  onNewPaymentDrawerOpen,
                  onNewPaymentDrawerClose,
                  isNewPaymentBatchDrawerOpen,
                  onNewPaymentBatchDrawerOpen,
                  onNewPaymentBatchDrawerClose,
                  isDetailsPaymentBatchDrawerOpen,
                  onDetailsPaymentBatchDrawerOpen,
                  onDetailsPaymentBatchDrawerClose,
              }}
          >
              {children}
          </PaymentContext.Provider>
      );
  };

  export function usePayment(): PaymentContextData {
    return useContext(PaymentContext);
  }

  export { PaymentProvider };
